import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import {
    ContentTagsCell,
    ContentTagsCellSkeleton,
    CreatorCell,
    CreatorCellSkeleton,
    creatorHandle,
    NotesPreviewCell,
    NotesPreviewCellSkeleton,
    PlatformCell,
    PlatformCellSkeleton,
    PriceOrStatusCell,
    PriceOrStatusCellSkeleton,
    type TableColumn,
} from "@/components/tableKit"
import type { CampaignType, Currency, PitchStatus, Platform, Talent } from "@/shared/types"

export type TalentRowStatus = "available" | "ineligible" | PitchStatus

export type TalentRow = {
    talent: Talent
    status: TalentRowStatus
    pitcherName?: string
    selected: boolean
    price: number | null
    guidePrice?: number
    selectable: boolean
    priceInvalid?: boolean
}

export type TalentSelection = Record<string, { price: number }>

export type TalentColumnsOptions = {
    platforms: Platform[]
    campaignType: CampaignType
    currency?: Currency
    onSelectionChange?: (talentId: string, entry: { price: number } | null) => void
    showNotes?: boolean
    hidePrices?: boolean
}

/** Checkbox and price call onSelectionChange; pages own the selected record. */
export function talentColumns({
    platforms,
    campaignType,
    currency = "USD",
    onSelectionChange,
    showNotes,
    hidePrices,
}: TalentColumnsOptions): TableColumn<TalentRow>[] {
    const columns: TableColumn<TalentRow>[] = []

    if (onSelectionChange) {
        columns.push({
            id: "select",
            header: "",
            className: "w-10",
            cell: (row) => (
                <Checkbox
                    checked={row.selected}
                    disabled={!row.selectable}
                    onCheckedChange={(checked) => {
                        if (checked !== true) {
                            onSelectionChange(row.talent.id, null)
                            return
                        }
                        const price = row.price || row.guidePrice
                        if (typeof price === "number" && price > 0) {
                            onSelectionChange(row.talent.id, { price })
                        }
                    }}
                    aria-label={`Select ${row.talent.name || "creator"}`}
                />
            ),
            skeleton: <Skeleton className="size-4" />,
        })
    }

    columns.push({
        id: "creator",
        header: "Creator",
        cell: (row) => <CreatorCell talent={row.talent} />,
        skeleton: <CreatorCellSkeleton />,
    })

    if (!hidePrices) {
        columns.push({
            id: "price",
            header: "Price",
            cell: (row) => (
                <PriceOrStatusCell
                    status={row.status}
                    price={row.price}
                    guidePrice={row.guidePrice}
                    currency={currency}
                    pitcherName={row.pitcherName}
                    prices={row.talent.localPrices}
                    platforms={platforms}
                    campaignType={campaignType}
                    creatorLabel={creatorHandle(row.talent) || row.talent.name}
                    disabled={!row.selectable}
                    invalid={row.priceInvalid}
                    onPriceChange={
                        onSelectionChange
                            ? (price) => onSelectionChange(row.talent.id, price ? { price } : null)
                            : undefined
                    }
                />
            ),
            skeleton: <PriceOrStatusCellSkeleton />,
        })
    }

    for (const platform of platforms) {
        columns.push({
            id: platform,
            header: platform,
            cell: (row) => <PlatformCell platform={platform} talent={row.talent} />,
            skeleton: <PlatformCellSkeleton />,
        })
    }

    columns.push({
        id: "content",
        header: "Content",
        cell: (row) => <ContentTagsCell tags={row.talent.contentTags} />,
        skeleton: <ContentTagsCellSkeleton />,
    })

    if (showNotes) {
        columns.push({
            id: "notes",
            header: "Notes",
            cell: (row) => <NotesPreviewCell notes={row.talent.listNotes} />,
            className: "min-w-56 max-w-96 whitespace-normal",
            skeleton: <NotesPreviewCellSkeleton />,
        })
    }

    return columns
}

/** Browse-table column rhythm when the page cannot build real columns yet. */
export const BROWSE_SKELETON_COLUMNS = talentColumns({
    platforms: ["TikTok", "Instagram", "YouTube"],
    campaignType: "Song",
})
